import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, BookMarked, BadgeCheck } from "lucide-react";
import { publications, doiLink } from "@/data/publications";
import { SectionHeading, Reveal } from "@/components/motion";

export const PublicationCard = ({ pub, index = 0 }) => (
  <Reveal delay={index % 3}>
    <article
      data-testid={`publication-card-${pub.id}`}
      className="group h-full bg-white rounded-2xl border border-slate-200/80 shadow-soft p-6 sm:p-7 flex flex-col hover:-translate-y-1 hover:shadow-lift hover:border-teal-500/30 transition-[transform,box-shadow,border-color] duration-300"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">
          <BookMarked size={12} /> {pub.year}
        </span>
        {pub.role === "Corresponding Author" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy-900 bg-navy-50 border border-navy-100 rounded-full px-3 py-1">
            <BadgeCheck size={12} className="text-teal-600" /> Corresponding Author
          </span>
        )}
      </div>
      <h3 className="mt-4 font-serif font-semibold text-slate-900 text-lg leading-snug">
        {pub.title}
      </h3>
      <p className="mt-2.5 text-sm font-medium text-navy-800">{pub.journal}</p>
      <p className="mt-1 text-xs font-mono text-slate-500">{pub.volume}</p>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1">Authors</span>
        {pub.authors}
      </p>
      <a
        href={doiLink(pub.doi)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`publication-doi-${pub.id}`}
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-900 transition-colors"
      >
        <ExternalLink size={14} className="shrink-0" />
        <span className="hidden sm:inline break-all">DOI: {pub.doi}</span>
        <span className="sm:hidden rounded-full border border-teal-200 bg-teal-50 px-4 py-1.5">View DOI</span>
      </a>
    </article>
  </Reveal>
);

export const Publications = () => (
  <section id="publications" className="bg-background py-16 sm:py-24 lg:py-32" data-testid="publications-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <SectionHeading
          number="03"
          eyebrow="Research & Publications"
          title="Academic Research & Publications"
          sub="Peer-reviewed research contributions in internal medicine and allied specialities."
        />
        <Reveal delay={2} className="shrink-0">
          <Link
            to="/publications"
            data-testid="publications-view-all-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy-900 hover:text-teal-700 transition-colors"
          >
            View all publications <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="publications-grid">
        {publications.map((pub, i) => (
          <PublicationCard key={pub.id} pub={pub} index={i} />
        ))}
      </div>
    </div>
  </section>
);
